import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getOutstandingFeeByClassReport } from '../../apis/payments/outstandingFeeByClassReport';
import { getSchoolClassesConfig } from '../../apis/schoolConfig';
import { RootState } from '../../redux/store';
import { SchoolClassesConfig } from '../../types/schoolConfigType';
import {
  mergeStudentRows,
  mergeClassWiseTotals,
  mergeGrandTotals
} from './utils';
import {
  StudentOutstandingDetail,
  ClassWiseTotal,
  GrandTotal,
  OutstandingFeeByClassApiResponse,
  OutstandingFeeFilters,
  OutstandingFeeReportParams
} from './types';
import OutstandingFeeByClassReport from './OutstandingFeeByClassReport';

const OutstandingFeeByClassReportContainer: React.FC = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const [allClasses, setAllClasses] = useState<SchoolClassesConfig>();
  const [studentRows, setStudentRows] = useState<
    StudentOutstandingDetail[] | null
  >(null);
  const [classWiseTotal, setClassWiseTotal] = useState<ClassWiseTotal[]>([]);
  const [grandTotal, setGrandTotal] = useState<GrandTotal | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getSchoolClassesConfig(loggedInUser.school).then((response) => {
      setAllClasses(response.data as unknown as SchoolClassesConfig);
    });
  }, [loggedInUser]);

  const onReset = () => {
    setStudentRows(null);
    setClassWiseTotal([]);
    setGrandTotal(null);
  };

  const onSearch = async (filters: OutstandingFeeFilters) => {
    const { asOnDate, session, grade, division } = filters;
    setLoading(true);
    const perPage = 100;
    let pageNo = 1;
    const allStudentOutstandingDetails: StudentOutstandingDetail[] = [];
    const classWiseTotalPerPage: ClassWiseTotal[][] = [];
    const grandTotalPerPage: GrandTotal[] = [];

    try {
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const params: OutstandingFeeReportParams = {
          asOnDate,
          session,
          grade,
          division,
          page: pageNo,
          perPage
        };
        const response = (await getOutstandingFeeByClassReport(
          params
        )) as unknown as OutstandingFeeByClassApiResponse;

        allStudentOutstandingDetails.push(
          ...(response.studentOutstandingDetails || [])
        );
        if (response.classWiseTotal?.length)
          classWiseTotalPerPage.push(response.classWiseTotal);
        if (response.grandTotal) grandTotalPerPage.push(response.grandTotal);
        if (!response.hasMore) break;
        pageNo += 1;
      }
      setStudentRows(mergeStudentRows(allStudentOutstandingDetails));
      setClassWiseTotal(mergeClassWiseTotals(classWiseTotalPerPage));
      setGrandTotal(mergeGrandTotals(grandTotalPerPage));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch outstanding fee by class report', err);
      setStudentRows([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OutstandingFeeByClassReport
      studentRows={studentRows}
      classWiseTotal={classWiseTotal}
      grandTotal={grandTotal}
      allClasses={allClasses as SchoolClassesConfig}
      onSearch={onSearch}
      onReset={onReset}
      loading={loading}
      school={loggedInUser.school}
    />
  );
};

export default OutstandingFeeByClassReportContainer;
